import { useEffect, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Check, X, MinusCircle, Loader2, ArrowRight } from "lucide-react"
import { cn } from "@/lib/utils"
import * as api from "../../api/apcm-ops.api"
import type { PatientBlocker, PatientBlockersResponse } from "../../api/apcm-ops.api"

interface Props {
  patientId: string
  orgId: string
}

export function BlockersPanel({ patientId, orgId }: Props) {
  const [data, setData] = useState<PatientBlockersResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!patientId || !orgId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .getPatientBlockers(patientId, orgId)
      .then((d) => !cancelled && setData(d))
      .catch(
        (e) =>
          !cancelled &&
          setError(e?.response?.data?.detail || e?.message || "Failed to load"),
      )
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [patientId, orgId])

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Pipeline Blockers</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Pipeline Blockers</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-destructive">
          {error || "Failed to load blockers"}
        </CardContent>
      </Card>
    )
  }

  const failedCount = data.blockers.filter((b) => b.passed === false).length

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          Pipeline Blockers
          {failedCount > 0 ? (
            <span className="rounded-full bg-destructive text-white px-1.5 py-px text-[10px] font-semibold leading-tight">
              {failedCount}
            </span>
          ) : (
            <span className="rounded-full bg-success text-white px-1.5 py-px text-[10px] font-semibold leading-tight">
              ok
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-xs">
        {/* Next action callout */}
        <div
          className={cn(
            "rounded-md px-2.5 py-2 border",
            failedCount > 0
              ? "bg-warning/10 border-warning/30 text-warning"
              : "bg-success/10 border-success/30 text-success",
          )}
        >
          <div className="flex items-start gap-1.5">
            <ArrowRight className="h-3 w-3 mt-0.5 shrink-0" />
            <span className="font-medium leading-relaxed">{data.next_action}</span>
          </div>
        </div>

        {/* Per-check rows */}
        <ul className="space-y-1.5">
          {data.blockers.map((b) => (
            <BlockerRow key={b.check} blocker={b} />
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

function BlockerRow({ blocker }: { blocker: PatientBlocker }) {
  const { icon, color } = statusStyle(blocker.passed)
  const Icon = icon
  return (
    <li className="flex items-start gap-2">
      <Icon className={cn("h-3.5 w-3.5 shrink-0 mt-0.5", color)} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-medium">{blocker.label}</span>
        </div>
        <p className="text-[11px] text-muted-foreground leading-relaxed mt-0.5">
          {blocker.detail}
        </p>
      </div>
    </li>
  )
}

function statusStyle(passed: boolean | null): { icon: any; color: string } {
  if (passed === true) return { icon: Check, color: "text-success" }
  if (passed === false) return { icon: X, color: "text-destructive" }
  return { icon: MinusCircle, color: "text-muted-foreground" }
}
